/**
 * Approve before spending (gaps plan §7).
 *
 * `work_plan.est_tokens` is persisted precisely so a run can be approved
 * before it costs anything — that is the pipeline plan's stated reason for
 * storing the plan rather than recomputing it. This dialog is the first
 * thing that uses it.
 *
 * The number is shown as what it is: **a lower bound**. Measured across the
 * 231 finished documents, `est_tokens` runs about 0.88× actual and 210 of
 * them came in *above* their estimate. Presenting that as a ceiling would be
 * wrong in the one direction that costs money, so the multiplier is printed
 * next to it rather than quietly applied.
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import type { ActionSpec } from '@server/jobs/actions.ts'
import type { EstimateResult } from '@server/jobs/estimate.ts'
import type { JobRecord } from '@server/jobs/store.ts'
import { ApiError, api } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count } from '~/i18n/format.ts'
import { Button, Label, Skeleton } from '~/components/ui/primitives.tsx'
import { Dialog } from '~/components/ui/overlay.tsx'
import { Command, ErrorState } from '~/components/ui/empty.tsx'

export function EstimateDialog({
  action, command, open, onClose, onStarted, enabled,
}: {
  action: ActionSpec | null
  /** The equivalent command line, shown when this build may not run it. */
  command: string | null
  open: boolean
  onClose: () => void
  onStarted: (job: JobRecord) => void
  enabled: boolean
}) {
  const { t, locale } = useT()

  const { data, error } = useQuery({
    queryKey: ['estimate', JSON.stringify(action)],
    queryFn: () =>
      api<{ estimate: EstimateResult }>('/api/jobs/estimate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(action),
      }),
    enabled: open && action !== null,
    staleTime: 60_000,
  })

  const start = useMutation({
    mutationFn: () =>
      api<{ job: JobRecord }>('/api/jobs', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(action),
      }),
    onSuccess: ({ job }) => { onStarted(job); onClose() },
  })

  const e = data?.estimate
  const n = (x: number) => count(x, locale)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('estimate.title')}
      closeLabel={t('action.close')}
      width={620}
      footer={
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onClose} variant="ghost">{t('estimate.cancel')}</Button>
          <div className="flex-1" />
          {start.error && (
            <span className="text-[length:var(--text-micro)] text-[var(--fail)]">
              {(start.error as ApiError).message}
            </span>
          )}
          <Button
            variant="primary"
            disabled={!enabled || !e || start.isPending}
            onClick={() => start.mutate()}
            title={enabled ? undefined : t('job.disabled')}
          >
            {t('estimate.confirm')}
          </Button>
        </div>
      }
    >
      {error ? (
        <ErrorState title={t('common.error')} detail={(error as ApiError).message} />
      ) : !e ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <Figure label={t('estimate.documents')} value={n(e.documents)} />
            <Figure
              label={t('estimate.pages')}
              value={e.pages === null ? '—' : n(e.pages)}
            />
            <Figure
              label={t('estimate.tokens')}
              value={
                e.free ? t('estimate.free')
                : e.estTokens === null ? t('estimate.unknown')
                : n(e.estTokens)
              }
              muted={e.estTokens === null}
            />
            <Figure
              label={t('estimate.cost')}
              value={
                e.free ? '$0.00'
                : e.costUsd === null ? t('estimate.noCost')
                : `$${e.costUsd.toFixed(2)}`
              }
              muted={e.costUsd === null}
            />
          </div>

          {/* The direction that costs money, said plainly. */}
          {!e.free && e.estTokens !== null && (
            <p className="rounded-[var(--radius-control)] border border-[var(--warn)]/30 bg-[var(--warn-bg)] px-2.5 py-1.5 text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text)]">
              <span className="font-medium">{t('estimate.lowerBound')}. </span>
              {e.note}
            </p>
          )}
          {(e.free || e.estTokens === null) && e.note && (
            <p className="max-w-[70ch] text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text-muted)]">
              {e.note}
            </p>
          )}

          {/*
            With nothing allowed to spawn, the estimate is still worth
            reading -- it is how someone decides whether to go and run this
            by hand -- so the dialog ends with the line to run rather than
            with a dead button and an apology.
          */}
          {!enabled && (
            <div>
              <p className="text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text-muted)]">
                {t('job.disabledWhy')}
              </p>
              {command && (
                <>
                  <div className="mt-2 text-[length:var(--text-micro)] text-[var(--text-faint)]">
                    {t('gaps.command')}
                  </div>
                  <Command>{command}</Command>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Dialog>
  )
}

function Figure({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <Label>{label}</Label>
      <div
        className={
          'tnum mt-0.5 text-[length:var(--text-title)] font-medium ' +
          (muted ? 'text-[var(--text-faint)]' : 'text-[var(--text)]')
        }
      >
        {value}
      </div>
    </div>
  )
}
